import React, { useEffect, useState } from 'react';

interface QueueHealth { name: string; depth: number; poisonDepth: number; }
interface ActiveSweep {
  sweepId: number;
  userId: string;
  email: string | null;
  status: string;
  totalSwept: number;
  createdAt: string;
  lastProgressedAt: string | null;
  stalledForMinutes: number | null;
}
interface ActiveDeletion {
  requestId: number;
  userId: string;
  email: string | null;
  status: string;
  requestedAt: string;
  runningForMinutes: number;
}
interface BackfillHealthUser {
  userId: string;
  email: string | null;
  lastIngestedAt: string | null;
  isIngesting: boolean;
  backfillPageToken: string | null;
  backfillStartedAt: string | null;
  failureCount: number;
  lastError: string | null;
  connectedAt: string | null;
  stalledForMinutes: number | null;
  emailCount: number;
  hasPoisonMessage: boolean;
  poisonChunkNumber: number | null;
}
interface ReauthHealthUser {
  userId: string;
  email: string | null;
  provider: string;
  updatedAt: string | null;
}
interface PendingDeletionUser {
  userId: string;
  email: string | null;
  disconnectedAt: string | null;
  subStatus: string | null;
  canceledAt: string | null;
  type: 'disconnecting' | 'lapsed';
  daysRemaining: number;
}
interface MicrosoftPushHealth {
  subscribed: number;
  notSubscribed: number;
  expired: number;
  fetchedAt: string;
}

interface PoisonMessage {
  messageId: string;
  isBackfill: boolean;
  userId: string | null;
  email: string | null;
  chunkNumber: number | null;
  totalIngested: number | null;
  deadSinceMinutes: number | null;
  dequeueCount: number;
  bodyPreview?: string;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
  return `${Math.floor(minutes / 1440)}d`;
}

interface SystemSetting { key: string; value: string; }

export default function AdminMonitoringPage() {
  const [llmBreakerOpen, setLlmBreakerOpen] = useState(false);
  const [llmBreakerResetting, setLlmBreakerResetting] = useState(false);
  const [llmBreakerStatus, setLlmBreakerStatus] = useState<string | null>(null);

  const [queueHealth, setQueueHealth] = useState<QueueHealth[] | null>(null);
  const [queueHealthLoading, setQueueHealthLoading] = useState(true);
  const [queueHealthFetchedAt, setQueueHealthFetchedAt] = useState<string | null>(null);

  const [activeSweeps, setActiveSweeps] = useState<ActiveSweep[] | null>(null);
  const [activeDeletions, setActiveDeletions] = useState<ActiveDeletion[] | null>(null);
  const [operationsLoading, setOperationsLoading] = useState(true);
  const [sweepActionStatus, setSweepActionStatus] = useState<string | null>(null);

  const [backfillHealth, setBackfillHealth] = useState<BackfillHealthUser[] | null>(null);
  const [backfillHealthLoading, setBackfillHealthLoading] = useState(true);

  const [reauthHealth, setReauthHealth] = useState<ReauthHealthUser[] | null>(null);
  const [reauthHealthLoading, setReauthHealthLoading] = useState(true);

  const [retentionHealth, setRetentionHealth] = useState<PendingDeletionUser[] | null>(null);
  const [retentionHealthLoading, setRetentionHealthLoading] = useState(true);

  const [msPushHealth, setMsPushHealth] = useState<MicrosoftPushHealth | null>(null);
  const [msPushLoading, setMsPushLoading] = useState(true);
  const [msPushBackfillStatus, setMsPushBackfillStatus] = useState<string | null>(null);

  const [poisonModal, setPoisonModal] = useState<string | null>(null);
  const [poisonMessages, setPoisonMessages] = useState<PoisonMessage[] | null>(null);
  const [poisonLoading, setPoisonLoading] = useState(false);
  const [poisonActionStatus, setPoisonActionStatus] = useState<string | null>(null);

  const [depthModal, setDepthModal] = useState<string | null>(null);
  const [depthMessages, setDepthMessages] = useState<PoisonMessage[] | null>(null);
  const [depthLoading, setDepthLoading] = useState(false);

  function loadQueueHealth() {
    setQueueHealthLoading(true);
    fetch('/api/mgmt/queue-health')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { queues: QueueHealth[]; fetchedAt: string }) => {
        setQueueHealth(data.queues);
        setQueueHealthFetchedAt(data.fetchedAt);
      })
      .catch(() => setQueueHealth([]))
      .finally(() => setQueueHealthLoading(false));
  }

  async function handleRestartSweep(sweepId: number) {
    setSweepActionStatus('Restarting…');
    try {
      const r = await fetch(`/api/mgmt/sweeps/${sweepId}/restart`, { method: 'POST' });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        setSweepActionStatus(`Failed: ${data.error ?? r.status}`);
        return;
      }
      setSweepActionStatus('Restarted — refreshing…');
      loadOperationsHealth();
    } catch {
      setSweepActionStatus('Request failed.');
    }
  }

  function loadOperationsHealth() {
    setOperationsLoading(true);
    fetch('/api/mgmt/operations-health')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { sweeps: ActiveSweep[]; deletions: ActiveDeletion[] }) => {
        setActiveSweeps(data.sweeps);
        setActiveDeletions(data.deletions);
      })
      .catch(() => { setActiveSweeps([]); setActiveDeletions([]); })
      .finally(() => setOperationsLoading(false));
  }

  function loadBackfillHealth() {
    setBackfillHealthLoading(true);
    fetch('/api/mgmt/backfill-health')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { users: BackfillHealthUser[] }) => setBackfillHealth(data.users))
      .catch(() => setBackfillHealth([]))
      .finally(() => setBackfillHealthLoading(false));
  }

  function loadReauthHealth() {
    setReauthHealthLoading(true);
    fetch('/api/mgmt/reauth-health')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { users: ReauthHealthUser[] }) => setReauthHealth(data.users))
      .catch(() => setReauthHealth([]))
      .finally(() => setReauthHealthLoading(false));
  }

  function loadRetentionHealth() {
    setRetentionHealthLoading(true);
    fetch('/api/mgmt/retention-health')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { users: PendingDeletionUser[] }) => setRetentionHealth(data.users))
      .catch(() => setRetentionHealth([]))
      .finally(() => setRetentionHealthLoading(false));
  }

  function loadMsPushHealth() {
    setMsPushLoading(true);
    fetch('/api/mgmt/microsoft-push-health')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: MicrosoftPushHealth) => setMsPushHealth(data))
      .catch(() => setMsPushHealth(null))
      .finally(() => setMsPushLoading(false));
  }

  async function handleMsPushBackfill() {
    setMsPushBackfillStatus('Subscribing…');
    try {
      const r = await fetch('/api/mgmt/subscribe-microsoft-push', { method: 'POST' });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setMsPushBackfillStatus(`Done: ${data.succeeded} subscribed, ${data.failed} failed of ${data.total}.`);
      loadMsPushHealth();
    } catch {
      setMsPushBackfillStatus('Request failed.');
    }
  }

  function openPoisonModal(queueName: string) {
    setPoisonModal(queueName);
    setPoisonMessages(null);
    setPoisonActionStatus(null);
    setPoisonLoading(true);
    fetch(`/api/mgmt/queues/${queueName}/poison`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { messages: PoisonMessage[] }) => setPoisonMessages(data.messages))
      .catch(() => setPoisonMessages([]))
      .finally(() => setPoisonLoading(false));
  }

  function closePoisonModal() {
    setPoisonModal(null);
    setPoisonMessages(null);
    setPoisonActionStatus(null);
  }

  function openDepthModal(queueName: string) {
    setDepthModal(queueName);
    setDepthMessages(null);
    setDepthLoading(true);
    fetch(`/api/mgmt/queues/${queueName}/messages`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: { messages: PoisonMessage[] }) => setDepthMessages(data.messages))
      .catch(() => setDepthMessages([]))
      .finally(() => setDepthLoading(false));
  }

  function closeDepthModal() {
    setDepthModal(null);
    setDepthMessages(null);
  }

  async function handleResume(userId: string) {
    setPoisonActionStatus(`Resuming…`);
    try {
      const r = await fetch(`/api/mgmt/users/${userId}/resume-backfill`, { method: 'POST' });
      if (!r.ok) throw new Error();
      setPoisonMessages(prev => prev?.filter(m => m.userId !== userId) ?? null);
      loadBackfillHealth();
      loadQueueHealth();
      setPoisonActionStatus('Resumed.');
    } catch {
      setPoisonActionStatus('Resume failed.');
    }
  }

  async function handleResumeAll() {
    if (!poisonMessages) return;
    const userIds = Array.from(new Set(poisonMessages.filter(m => m.userId).map(m => m.userId!)));
    let done = 0;
    for (const uid of userIds) {
      setPoisonActionStatus(`Resuming ${done + 1}/${userIds.length}…`);
      try {
        await fetch(`/api/mgmt/users/${uid}/resume-backfill`, { method: 'POST' });
        done++;
      } catch { /* continue */ }
    }
    setPoisonMessages([]);
    loadBackfillHealth();
    loadQueueHealth();
    setPoisonActionStatus(`Resumed ${done}/${userIds.length}.`);
  }

  async function handleClearAll() {
    if (!poisonModal) return;
    setPoisonActionStatus('Clearing…');
    try {
      const r = await fetch(`/api/mgmt/queues/${poisonModal}/poison`, { method: 'DELETE' });
      const data = await r.json();
      setPoisonMessages([]);
      loadQueueHealth();
      setPoisonActionStatus(`Cleared ${data.deleted} message(s).`);
    } catch {
      setPoisonActionStatus('Clear failed.');
    }
  }

  function loadLlmBreakerStatus() {
    fetch('/api/mgmt/settings')
      .then(r => r.ok ? r.json() : Promise.reject())
      .then((data: SystemSetting[]) => {
        const setting = data.find(s => s.key === 'llm_circuit_breaker_open');
        setLlmBreakerOpen(setting?.value?.toLowerCase() === 'true');
      })
      .catch(() => {});
  }

  async function handleResetLlmBreaker() {
    setLlmBreakerResetting(true);
    setLlmBreakerStatus(null);
    try {
      const r = await fetch('/api/mgmt/llm-breaker/reset', { method: 'POST' });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setLlmBreakerOpen(false);
      setLlmBreakerStatus(`LLM re-enabled. Label backfill queued for ${data.usersEnqueued} user(s) to recover any missed To Respond labels.`);
    } catch {
      setLlmBreakerStatus('Reset failed.');
    } finally {
      setLlmBreakerResetting(false);
    }
  }

  useEffect(() => {
    loadLlmBreakerStatus();
    loadQueueHealth();
    loadOperationsHealth();
    loadBackfillHealth();
    loadReauthHealth();
    loadRetentionHealth();
    loadMsPushHealth();
  }, []);

  const isBackfillQueue = poisonModal === 'backfill-queue';

  return (
    <>
      <div className="page-header">
        <h1>Monitoring</h1>
      </div>

      {/* ── LLM circuit breaker warning ── */}
      {(llmBreakerOpen || llmBreakerStatus) && (
        <div className="dashboard-card" style={{ marginBottom: 24, borderLeft: '4px solid var(--danger)', background: 'rgba(220,38,38,0.06)' }}>
          {llmBreakerOpen && (
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--danger)', marginBottom: 4 }}>
                  LLM circuit breaker is open — Anthropic API calls are paused
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  Add credits to your Anthropic account, then click <strong>Re-enable LLM</strong> to resume classification.
                </div>
              </div>
              <button
                className="btn-primary"
                style={{ fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={handleResetLlmBreaker}
                disabled={llmBreakerResetting}
              >
                {llmBreakerResetting ? 'Re-enabling…' : 'Re-enable LLM'}
              </button>
            </div>
          )}
          {llmBreakerStatus && (
            <p style={{ fontSize: 13, color: llmBreakerStatus.startsWith('Reset failed') ? 'var(--danger)' : 'var(--success)', marginTop: llmBreakerOpen ? 10 : 0 }}>
              {llmBreakerStatus}
            </p>
          )}
        </div>
      )}

      {/* ── Queue health ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">System health</div>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={loadQueueHealth} disabled={queueHealthLoading}>
            {queueHealthLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {queueHealthLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
        ) : !queueHealth || queueHealth.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No queue data available.</p>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Queue', 'Depth', 'Poison'].map(h => (
                    <th key={h} style={{ textAlign: h === 'Queue' ? 'left' : 'right', padding: '2px 8px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {queueHealth.map(q => (
                  <tr key={q.name} style={{ background: q.poisonDepth > 0 ? 'rgba(220,38,38,0.07)' : undefined }}>
                    <td style={{ padding: '4px 8px', fontFamily: 'monospace', fontSize: 12 }}>{q.name}</td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {q.depth > 0 ? (
                        <button
                          onClick={() => openDepthModal(q.name)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            color: 'var(--accent)', fontWeight: 600, fontSize: 13,
                            textDecoration: 'underline', textDecorationStyle: 'dotted',
                          }}
                        >
                          {q.depth.toLocaleString()}
                        </button>
                      ) : q.depth}
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {q.poisonDepth > 0 ? (
                        <button
                          onClick={() => openPoisonModal(q.name)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            color: 'var(--danger)', fontWeight: 600, fontSize: 13,
                            textDecoration: 'underline', textDecorationStyle: 'dotted',
                          }}
                        >
                          ⚠ {q.poisonDepth}
                        </button>
                      ) : q.poisonDepth}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            {queueHealth.some(q => q.poisonDepth > 0) && (
              <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 8 }}>Poison messages detected — click a count to inspect and act.</p>
            )}
            {queueHealthFetchedAt && (
              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>Fetched {new Date(queueHealthFetchedAt).toLocaleTimeString()}</p>
            )}
          </>
        )}
      </div>

      {/* ── Active operations ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">
            Active operations
            {((activeSweeps && activeSweeps.length > 0) || (activeDeletions && activeDeletions.length > 0)) && (
              <span style={{
                marginLeft: 8,
                background: (activeSweeps ?? []).some(s => s.stalledForMinutes != null) ? 'var(--danger)' : 'var(--accent)',
                color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600,
              }}>
                {(activeSweeps?.length ?? 0) + (activeDeletions?.length ?? 0)}
              </span>
            )}
          </div>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={loadOperationsHealth} disabled={operationsLoading}>
            {operationsLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {operationsLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
        ) : (activeSweeps?.length ?? 0) + (activeDeletions?.length ?? 0) === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No active operations.</p>
        ) : (
          <>
            {activeSweeps && activeSweeps.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Inbox sweeps</div>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse', marginBottom: activeDeletions && activeDeletions.length > 0 ? 16 : 0 }}>
                  <thead>
                    <tr>
                      {['User', 'Phase', 'Swept', 'Started', 'Last progress', 'Status', ''].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '2px 8px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeSweeps.map(s => (
                      <tr key={s.sweepId} style={{ borderTop: '1px solid var(--border)', background: s.stalledForMinutes != null ? 'rgba(220,38,38,0.07)' : undefined }}>
                        <td style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12 }}>{s.email ?? s.userId}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{s.userId}</div>
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{s.status}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{s.totalSwept.toLocaleString()}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(s.createdAt)}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {s.lastProgressedAt ? formatDate(s.lastProgressedAt) : '—'}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>
                          {s.stalledForMinutes != null ? (
                            <span style={{ color: 'var(--danger)' }}>Stalled {formatDuration(s.stalledForMinutes)}</span>
                          ) : (
                            <span style={{ color: 'var(--accent)' }}>Running</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 8px' }}>
                          {s.stalledForMinutes != null && (
                            <button
                              className="btn-secondary"
                              style={{ fontSize: 11, padding: '2px 8px' }}
                              onClick={() => handleRestartSweep(s.sweepId)}
                            >
                              Restart
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                {sweepActionStatus && (
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>{sweepActionStatus}</p>
                )}
              </>
            )}
            {activeDeletions && activeDeletions.length > 0 && (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Data deletions</div>
                <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['User', 'Status', 'Requested', 'Running for'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '2px 8px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeDeletions.map(d => (
                      <tr key={d.requestId} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12 }}>{d.email ?? d.userId}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{d.userId}</div>
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{d.status}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>{formatDate(d.requestedAt)}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{formatDuration(d.runningForMinutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* ── Backfill health ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">
            Backfill health
            {backfillHealth && backfillHealth.length > 0 && (
              <span style={{ marginLeft: 8, background: 'var(--danger)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                {backfillHealth.length}
              </span>
            )}
          </div>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={loadBackfillHealth} disabled={backfillHealthLoading}>
            {backfillHealthLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {backfillHealthLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
        ) : !backfillHealth || backfillHealth.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>All backfills complete.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['User', 'Last ingested', 'Emails', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '2px 8px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {backfillHealth.map(u => (
                <tr key={u.userId}>
                  <td style={{ padding: '4px 8px' }}>
                    <div style={{ fontSize: 12 }}>{u.email ?? u.userId}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{u.userId}</div>
                  </td>
                  <td style={{ padding: '4px 8px', fontSize: 12 }}>{u.lastIngestedAt ? formatDate(u.lastIngestedAt) : 'Never'}</td>
                  <td style={{ padding: '4px 8px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{u.emailCount.toLocaleString()}</td>
                  <td style={{ padding: '4px 8px', fontSize: 12 }}>
                    {(() => {
                      const recentMs = 15 * 60 * 1000;
                      const fresh = !!u.lastIngestedAt && (Date.now() - new Date(u.lastIngestedAt).getTime()) < recentMs;
                      if (u.isIngesting || (u.backfillPageToken && fresh)) {
                        return <span style={{ color: 'var(--accent)' }}>In progress</span>;
                      }
                      if (u.backfillPageToken) {
                        const duration = formatDuration(u.stalledForMinutes ?? 0);
                        return (
                          <span style={{ color: 'var(--danger)' }}>
                            Stalled {duration}
                            {u.failureCount > 0 && <span style={{ marginLeft: 6, fontSize: 11 }}>— {u.failureCount} failure(s){u.lastError ? `: ${u.lastError.slice(0, 80)}` : ''}</span>}
                            {u.hasPoisonMessage && (
                              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.85 }}>
                                — dead-lettered{u.poisonChunkNumber != null ? ` at chunk ${u.poisonChunkNumber}` : ''}
                              </span>
                            )}
                          </span>
                        );
                      }
                      const neverStartedMs = u.connectedAt ? Date.now() - new Date(u.connectedAt).getTime() : 0;
                      const neverStartedMins = Math.floor(neverStartedMs / 60000);
                      return (
                        <span style={{ color: 'var(--danger)' }}>
                          Never started
                          {neverStartedMins > 0 && <span style={{ fontSize: 11, marginLeft: 4, opacity: 0.85 }}>({formatDuration(neverStartedMins)} since connect)</span>}
                        </span>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* ── Disconnected accounts ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">
            Disconnected accounts
            {reauthHealth && reauthHealth.length > 0 && (
              <span style={{ marginLeft: 8, background: 'var(--danger)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                {reauthHealth.length}
              </span>
            )}
          </div>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={loadReauthHealth} disabled={reauthHealthLoading}>
            {reauthHealthLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {reauthHealthLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
        ) : !reauthHealth || reauthHealth.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No disconnected accounts.</p>
        ) : (
          <>
            <p style={{ fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>
              These users have invalid or revoked refresh tokens. Sync is paused for them until they reconnect.
            </p>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['User', 'Provider', 'Flagged at'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '2px 8px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reauthHealth.map(u => (
                  <tr key={`${u.userId}-${u.provider}`} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ fontSize: 12 }}>{u.email ?? u.userId}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{u.userId}</div>
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 12 }}>{u.provider}</td>
                    <td style={{ padding: '6px 8px', fontSize: 12 }}>{formatDate(u.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      {/* ── Pending deletion ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">
            Pending deletion
            {retentionHealth && retentionHealth.length > 0 && (
              <span style={{ marginLeft: 8, background: 'var(--warning, #f59e0b)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                {retentionHealth.length}
              </span>
            )}
          </div>
          <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={loadRetentionHealth} disabled={retentionHealthLoading}>
            {retentionHealthLoading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {retentionHealthLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
        ) : !retentionHealth || retentionHealth.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No users approaching data retention window.</p>
        ) : (
          <>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
              Disconnected users (30-day window) and lapsed subscribers (grace period) whose data will be purged when the window expires.
            </p>
            <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['User', 'Type', 'Reason', 'Days remaining'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '2px 8px 6px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {retentionHealth.map(u => (
                  <tr key={u.userId} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px' }}>
                      <div style={{ fontSize: 12 }}>{u.email ?? u.userId}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{u.userId}</div>
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 12 }}>{u.type}</td>
                    <td style={{ padding: '6px 8px', fontSize: 12 }}>
                      {u.type === 'disconnecting'
                        ? `Disconnected ${formatDate(u.disconnectedAt)}`
                        : `Subscription ${u.subStatus} since ${formatDate(u.canceledAt)}`}
                    </td>
                    <td style={{ padding: '6px 8px', fontSize: 12, color: u.daysRemaining <= 7 ? 'var(--danger)' : undefined, fontWeight: u.daysRemaining <= 7 ? 600 : undefined }}>
                      {u.daysRemaining > 0 ? `${u.daysRemaining}d` : 'Eligible for purge'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      {/* ── Microsoft push health ── */}
      <div className="dashboard-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="card-label">
            Microsoft push subscriptions
            {msPushHealth && msPushHealth.expired > 0 && (
              <span style={{ marginLeft: 8, background: 'var(--danger)', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, fontWeight: 600 }}>
                {msPushHealth.expired} expired
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={loadMsPushHealth} disabled={msPushLoading}>
              {msPushLoading ? 'Loading…' : 'Refresh'}
            </button>
            <button className="btn-secondary" style={{ fontSize: 12, padding: '2px 10px' }} onClick={handleMsPushBackfill} disabled={!!msPushBackfillStatus?.includes('…')}>
              Subscribe all
            </button>
          </div>
        </div>
        {msPushLoading ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
        ) : !msPushHealth ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No Microsoft accounts.</p>
        ) : (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }}>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>On push:</span>{' '}
              <strong style={{ color: msPushHealth.subscribed > 0 ? 'var(--success)' : undefined }}>{msPushHealth.subscribed}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>On polling:</span>{' '}
              <strong style={{ color: msPushHealth.notSubscribed > 0 ? 'var(--warning, #d97706)' : undefined }}>{msPushHealth.notSubscribed}</strong>
            </div>
            {msPushHealth.expired > 0 && (
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Expired:</span>{' '}
                <strong style={{ color: 'var(--danger)' }}>{msPushHealth.expired}</strong>
              </div>
            )}
            {msPushHealth.fetchedAt && (
              <div style={{ color: 'var(--text-secondary)', fontSize: 11 }}>
                Fetched {new Date(msPushHealth.fetchedAt).toLocaleTimeString()}
              </div>
            )}
          </div>
        )}
        {msPushBackfillStatus && (
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>{msPushBackfillStatus}</p>
        )}
      </div>

      {/* ── Depth peek modal ── */}
      {depthModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={e => { if (e.target === e.currentTarget) closeDepthModal(); }}
        >
          <div style={{
            background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.22)', width: '100%', maxWidth: 820,
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{depthModal}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Active messages — read-only peek (up to 32)
                </div>
              </div>
              <button onClick={closeDepthModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-secondary)', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
              {depthLoading ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
              ) : !depthMessages || depthMessages.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No messages found.</p>
              ) : depthModal === 'backfill-queue' ? (
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['User', 'Chunk', 'Ingested', 'In queue'].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '2px 8px 8px', color: 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {depthMessages.map(m => (
                      <tr key={m.messageId} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12 }}>{m.email ?? m.userId ?? '—'}</div>
                          {m.userId && <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{m.userId}</div>}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{m.chunkNumber ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{m.totalIngested?.toLocaleString() ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {m.deadSinceMinutes != null ? formatDuration(m.deadSinceMinutes) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Body preview', 'In queue'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '2px 8px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {depthMessages.map(m => (
                      <tr key={m.messageId} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, maxWidth: 600, wordBreak: 'break-all' }}>{m.bodyPreview ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {m.deadSinceMinutes != null ? formatDuration(m.deadSinceMinutes) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-secondary" style={{ fontSize: 12 }} onClick={closeDepthModal}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Poison queue modal ── */}
      {poisonModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={e => { if (e.target === e.currentTarget) closePoisonModal(); }}
        >
          <div style={{
            background: 'var(--surface)', borderRadius: 'var(--radius-lg)',
            boxShadow: '0 24px 64px rgba(0,0,0,0.22)', width: '100%', maxWidth: 820,
            maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 15 }}>{poisonModal}-poison</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                  Dead-lettered messages — inspect, resume, or clear
                </div>
              </div>
              <button onClick={closePoisonModal} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: 'var(--text-secondary)', lineHeight: 1, padding: '0 4px' }}>×</button>
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', flex: 1, padding: '16px 20px' }}>
              {poisonLoading ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
              ) : !poisonMessages || poisonMessages.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>No messages in poison queue.</p>
              ) : isBackfillQueue ? (
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['User', 'Chunk', 'Ingested', 'Dead since', 'Retries', ''].map((h, i) => (
                        <th key={i} style={{ textAlign: 'left', padding: '2px 8px 8px', color: 'var(--text-secondary)', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {poisonMessages.map(m => (
                      <tr key={m.messageId} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px' }}>
                          <div style={{ fontSize: 12 }}>{m.email ?? m.userId ?? '—'}</div>
                          {m.userId && <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{m.userId}</div>}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{m.chunkNumber ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>{m.totalIngested?.toLocaleString() ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {m.deadSinceMinutes != null ? formatDuration(m.deadSinceMinutes) : '—'}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{m.dequeueCount}</td>
                        <td style={{ padding: '6px 8px' }}>
                          {m.userId && (
                            <button
                              className="btn-secondary"
                              style={{ fontSize: 11, padding: '2px 8px' }}
                              onClick={() => handleResume(m.userId!)}
                            >
                              Resume
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {['Body preview', 'Dead since', 'Retries'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '2px 8px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {poisonMessages.map(m => (
                      <tr key={m.messageId} style={{ borderTop: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: 11, maxWidth: 500, wordBreak: 'break-all' }}>{m.bodyPreview ?? '—'}</td>
                        <td style={{ padding: '6px 8px', fontSize: 12, whiteSpace: 'nowrap' }}>
                          {m.deadSinceMinutes != null ? formatDuration(m.deadSinceMinutes) : '—'}
                        </td>
                        <td style={{ padding: '6px 8px', fontSize: 12 }}>{m.dequeueCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
              {isBackfillQueue && !!poisonMessages?.length && (
                <button className="btn-primary" style={{ fontSize: 12 }} onClick={handleResumeAll}>
                  Resume all
                </button>
              )}
              <button
                className="btn-secondary"
                style={{ fontSize: 12, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                onClick={handleClearAll}
              >
                Clear all
              </button>
              {poisonActionStatus && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 4 }}>{poisonActionStatus}</span>
              )}
              <div style={{ flex: 1 }} />
              <button className="btn-secondary" style={{ fontSize: 12 }} onClick={closePoisonModal}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
